import CallError from '../../utils/CallError';
import { isGoldApp, isPlatinumApp, isSilverApp, sortByPartnerLevelPayGrade } from '../../utils/apps';
import { shuffleArray } from '../../utils/general';
import { settings } from './setup';

const splitGoldPlatinum = (all, app) => {
  let chunk;
  if (isGoldApp(app)) {
    chunk = 0;
  } else if (isPlatinumApp(app)) {
    chunk = 1;
  }
  all[chunk] = (all[chunk] ?? []).concat(app);
  return all;
};

/**
 * This function will sort the array based on the partner level pay grade
 * and then split the array into two arrays, one for platinum and one for gold,
 * shuffle the arrays and then return the arrays concatenated together
 * @param {any[]} apps Array of apps
 * @returns {any[]} The array of apps sorted by partner level and group shuflled
 */
const prepareArray = (apps) =>
  apps
    .sort(sortByPartnerLevelPayGrade)
    .reduce(splitGoldPlatinum, [])
    .map((chunk) => shuffleArray(chunk))
    .flat();

/**
 * Function to get carousel apps for a specific product.
 * @callback GPA
 * @param {{key: string, cloud: string}={key="All"}} selectedProduct The product you want to get the carousel apps for.
 * @returns {any[]}
 */
export function getProductApps({ key, cloud } = { key: 'All' }) {
  if (this[key]) {
    return this[key];
  } else if (cloud.toLowerCase() === 'ec') {
    this[key] = shuffleArray(this.original.filter((app) => isGoldApp(app))).slice(0, 50);
    if (this[key].length > 10) {
      return this[key];
    } else {
      this[key] = prepareArray(this.original.filter((app) => isGoldApp(app) || isPlatinumApp(app))).slice(0, 10);
      return this[key];
    }
  } else if (cloud.toLowerCase() === 'dc') {
    this[key] = shuffleArray(this.dc).slice(0, 10);
    return this[key];
  } else if (cloud.toLowerCase() === 'cc') {
    this[key] = shuffleArray(this.cc).slice(0, 10);
    return this[key];
  }
}
/**
 * Get featured apps for Browse.
 * @param {('CC'|'EC')} [params.cloud='EC'] - the cloud you want the featured apps for
 * @returns {Promise<{original: any[], All: any[], ec: any[], dc: any[], getProductApps: GPA}>}
 */
export default async (params = {}, signal = null) => {
  let apps, isResponseOk, badResponse;
  const { cloud: cloudParam = 'EC', authToken } = params;
  const cloud = cloudParam.toUpperCase();
  const headers = new Headers({
    'x-api-key': settings.apiKey,
    Accept: 'application/vnd.adobe.xt.app-card-v1+json',
  });

  if (authToken) {
    headers.append('Authorization', authToken);
  }

  const fetchParams = {
    signal,
    headers,
  };

  if (cloud === 'EC') {
    const [ecResponse, dcResponse] = await Promise.all([
      fetch(`${settings.endpoint}/v2/apps?featured=true&type=EC`, fetchParams),
      fetch(`${settings.endpoint}/v2/apps?featured=true&type=DC`, fetchParams),
    ]);
    isResponseOk = ecResponse.ok && dcResponse.ok;
    if (!ecResponse.ok) {
      badResponse = ecResponse;
    }
    if (!dcResponse.ok) {
      badResponse = dcResponse;
    }
    const content = await Promise.all([ecResponse.text(), dcResponse.text()]);
    try {
      apps = content.map((text) => JSON.parse(text).cloudApps).flat();
    } catch (e) {
      // response is not json
    }
  } else if (cloud === 'CC') {
    const ccResponse = await fetch(`${settings.endpoint}/v2/apps?featured=true&type=CC`, fetchParams);
    isResponseOk = ccResponse.ok;
    if (!isResponseOk) {
      badResponse = ccResponse;
    }
    const content = await ccResponse.text();
    try {
      apps = JSON.parse(content).cloudApps;
    } catch (e) {
      // response is not json
    }
  }

  if (!isResponseOk) {
    throw new CallError('http', apps, badResponse.status, { message: badResponse.statusText });
  } else {
    const result = {
      original: apps,
      ec: cloud === 'EC' ? apps.filter((app) => app.type.toLowerCase() === 'ec') : [],
      dc: cloud === 'EC' ? apps.filter((app) => app.type.toLowerCase() === 'dc') : [],
      cc: cloud === 'CC' ? apps : [], // no filter needed for cc because apps only contains cc apps when cloud === CC
      getProductApps,
    };
    result.getProductApps({ key: 'All', cloud });
    return result;
  }
};
